import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import { apiBaseUrl } from '../utils/urls';
import localAdapter, { cacheAdapter, isLocalRequest, offlineModeAdapter } from '../offline/localAdapter';
import { isOffline, isOfflineMode, markServerReachable } from '../offline/network';
import { isCacheablePath, normalizeApiPath, readRefCache, writeRefCache } from '../offline/refCache';

export function isNativeApp() {
  return Capacitor.isNativePlatform();
}

function isCacheableGet(config) {
  return String(config?.method || 'get').toLowerCase() === 'get' && isCacheablePath(normalizeApiPath(config?.url));
}

const api = axios.create({
  baseURL: apiBaseUrl(),
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  // التطبيق يعرّف نفسه للخادم، فيصدر له جلسة لا تنتهي إلا بتسجيل الخروج.
  if (isNativeApp()) {
    config.headers['X-Client-Platform'] = 'app';
  }
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  const isFormData = typeof FormData !== 'undefined' && config.data instanceof FormData;
  const isBlob = typeof Blob !== 'undefined' && config.data instanceof Blob;
  const isPlainObject = config.data
    && typeof config.data === 'object'
    && !Array.isArray(config.data)
    && !isFormData
    && !isBlob;

  if (isPlainObject) {
    config.headers['Content-Type'] = 'application/json';
    config.data = JSON.stringify(config.data);
  }

  // الفحص المحفوظ على الجهاز (رقمه يبدأ بـ L) يُجاب من الجهاز، وكذلك إنشاء فحص دون اتصال.
  if (isLocalRequest(config) || config.offlineCreate) {
    config.adapter = localAdapter;
  } else if (isOfflineMode()) {
    // الوضع أوفلاين: البيانات المرجعية من الجهاز، وكل ما عداها مرفوض دون أن يخرج الطلب.
    config.adapter = isCacheableGet(config) ? cacheAdapter : offlineModeAdapter;
  } else if (isOffline() && isCacheableGet(config)) {
    // الشبكة مقطوعة أو الخادم لا يُصل إليه: نقرأ من الجهاز مباشرة بدل انتظار مهلة كل طلب.
    // فحص دوري في network.js يعيد الطلبات إلى الخادم عند عودته.
    config.adapter = cacheAdapter;
  }

  return config;
});

api.interceptors.response.use(
  (response) => {
    const fromDevice = response?.request?.local || response?.request?.cache;
    if (!fromDevice) {
      markServerReachable(true);
      if (isCacheableGet(response.config)) {
        writeRefCache(normalizeApiPath(response.config.url), response.data);
      }
    }
    return response;
  },
  async (error) => {
    const status = error?.response?.status;
    const requestUrl = error?.config?.url || '';
    const isTimeout = error?.code === 'ECONNABORTED' || error?.message?.includes('timeout');
    const hasNoHttpResponse = !error?.response;
    const fromDevice = [localAdapter, cacheAdapter, offlineModeAdapter].includes(error?.config?.adapter);

    // الخادم لا يُصل إليه: البيانات المرجعية تُقرأ من نسخة الجهاز إن وُجدت.
    if (hasNoHttpResponse && !fromDevice) {
      markServerReachable(false);
      if (isCacheableGet(error.config)) {
        const cached = await readRefCache(normalizeApiPath(error.config.url));
        if (cached !== undefined) {
          return { data: cached, status: 200, statusText: 'OK', headers: {}, config: error.config, request: { cache: true } };
        }
      }
    }

    // لا تقم بإجبار المستخدم على الخروج عند فشل login نفسه
    const isLoginRequest = requestUrl.includes('/auth/login');

    // لا نعيد المحاولة تلقائيًا عند timeout حتى لا يتضاعف زمن الانتظار على المستخدم
    // (كان المستخدم ينتظر مهلة كاملة مرتين متتاليتين في حالات التعثر).

    if (status === 401 && !isLoginRequest) {
      localStorage.removeItem('token');
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }

    if (fromDevice) {
      // رسالة الحفظ على الجهاز جاهزة من المحوّل.
    } else if (isTimeout) {
      error.userMessage = 'الخادم استغرق وقتًا أطول من المتوقع. حاول مرة أخرى.';
    } else if (hasNoHttpResponse) {
      error.userMessage = 'تعذر الاتصال بالخادم (Network Error). تأكد أن خادم API يعمل وأن عنوان API صحيح.';
    }

    return Promise.reject(error);
  }
);

export default api;
